// \src\examples\Navbars\DashboardNavbar

/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================
*/

import { useState, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";

import api, { handleApi } from "pages/api/api";

import MenuSearch from "pages/components/MenuSearch";
import DocsMenu from "pages/components/DocsMenu";
import UserMenu from "pages/components/UserMenu";
import EmailDialog from "pages/components/EmailDialog";
import HeaderTypeSwitch from "pages/components/HeaderTypeSwitch";
import ClientesPagadoresQuickViewButton from "pages/components/ClientesPagadoresQuickView";
import EquipamentosEventosAlertasPopper from "pages/components/EquipamentosEventosAlertasPopper";
import ClientesDebitosAlertasPopper from "pages/components/ClientesDebitosAlertasPopper";
import DialogClienteDebitoInfo from "pages/components/DialogClienteDebitoInfo";
import DialogClienteContactos from "pages/components/DialogClienteContactos";

import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import Stack from "@mui/material/Stack";
import Badge from "@mui/material/Badge";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";

import TelegramIcon from "@mui/icons-material/Send"; // fallback
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import DialogBarcodeReader from "pages/components/DialogBarcodeReader";

import MDBox from "components/MDBox";

import Breadcrumbs from "examples/Breadcrumbs";
import NotificationItem from "examples/Items/NotificationItem";

import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

import { useMaterialUIController, setMiniSidenav, setOpenConfigurator } from "context";
import { useAuth } from "pages/context/AuthContext";
import { useAlert } from "pages/context/AlertContext";

// ---------- pequenos utilitários ----------
const asNumber = (v) => {
  if (v === null || v === undefined) return 0;
  if (typeof v === "number") return v;
  const s = String(v).replace(/\./g, "").replace(",", "."); // 1.234,56 → 1234.56
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
};

const TelegramSvgIcon = () => (
  <img
    src="https://cdn.jsdelivr.net/npm/simple-icons@v9/icons/telegram.svg"
    alt="Telegram"
    style={{
      width: 18,
      height: 18,
      filter:
        "invert(38%) sepia(98%) saturate(747%) hue-rotate(176deg) brightness(95%) contrast(101%)",
    }}
  />
);

function DashboardNavbar({ absolute, light, isMini }) {
  const theme = useTheme();
  const { userData, temFuncao } = useAuth();
  const { showAlert } = useAlert();

  const isAdminLike =
    temFuncao("Administração") || temFuncao("Informática") || temFuncao("Tesouraria");

  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    transparentNavbar,
    fixedNavbar,
    openConfigurator,
    darkMode,
    uiOverrides,
    getGradientFromColor,
    getTextColorForBackground,
    sidenavColor,
  } = controller;

  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");

  const [openMenu, setOpenMenu] = useState(false);
  const [openChatsMenu, setOpenChatsMenu] = useState(null);
  const [chatsNaoLidos, setChatsNaoLidos] = useState(0);
  const [chatsMencoes, setChatsMencoes] = useState(0);
  const [chatsNaoLidosLista, setChatsNaoLidosLista] = useState([]);

  const [telegramLigado, setTelegramLigado] = useState(false);
  const [resumoTelegram, setResumoTelegram] = useState(true);
  const [resumoEmail, setResumoEmail] = useState(true);

  const navigate = useNavigate();
  const route = useLocation().pathname.split("/").slice(1);

  const [openEmail, setOpenEmail] = useState(false);

  const [openBarcodeReader, setOpenBarcodeReader] = useState(false);

  const [openClienteDebitoInfo, setOpenClienteDebitoInfo] = useState(false);
  const [clienteDebitoSelecionado, setClienteDebitoSelecionado] = useState(null);

  const [openContactos, setOpenContactos] = useState(false);
  const [itemContactos, setItemContactos] = useState(null);

  const handleOpenContactos = (item) => {
    setItemContactos(item || null);
    setOpenContactos(true);
    setOpenClienteDebitoInfo(false);
    setClienteDebitoSelecionado(null);
  };

  const carregarResumoChats = async () => {
    if (!userData?.id) {
      setChatsNaoLidos(0);
      setChatsMencoes(0);
      setChatsNaoLidosLista([]);
      return;
    }

    const [data, err] = await handleApi(
      api.post("Chats/SelResumoChatsNaoLidos", {
        idutilizador: userData.id,
      })
    );

    if (err) {
      setChatsNaoLidos(0);
      setChatsMencoes(0);
      setChatsNaoLidosLista([]);
      return;
    }

    setChatsNaoLidos(Number(data?.total_nao_lidas || 0));
    setChatsNaoLidosLista(Array.isArray(data?.chats) ? data.chats : []);
    setChatsMencoes(Number(data?.total_mencoes || 0));
  };

  useEffect(() => {
    setNavbarType(fixedNavbar ? "sticky" : "static");
  }, [fixedNavbar]);

  const carregarPreferencias = async () => {
    if (!userData?.id) return;

    const [data, err] = await handleApi(
      api.post("Chats/SelPreferenciasNotificacoesChats", {
        idutilizador: userData.id,
      })
    );

    if (!err && data) {
      setTelegramLigado(data.telegram_ligado === true || Number(data.telegram_ativo || 0) === 1);
      setResumoTelegram(Number(data.telegram_resumo_ativo || 0) === 1);
      setResumoEmail(Number(data.email_resumo_chats_ativo ?? 1) === 1);
    }
  };

  const validarPreferencias = (telegram, email) => {
    if (!telegram && !email) {
      showAlert(
        "Tem de manter pelo menos uma via de notificação ativa (Telegram ou E-mail), para continuar informado sobre mensagens por ler.",
        "warning"
      );

      return null;
    }

    return {
      telegram,
      email,
    };
  };

  const atualizarPreferencias = async (novos) => {
    const payload = {
      idutilizador: userData.id,
      telegram_resumo_ativo: novos.resumoTelegram ? 1 : 0,
      email_resumo_chats_ativo: novos.resumoEmail ? 1 : 0,
    };

    await handleApi(api.post("Chats/UpdPreferenciasNotificacoesChats", payload));
  };

  useEffect(() => {
    carregarResumoChats();
    carregarPreferencias();

    const timer = setInterval(() => {
      carregarResumoChats();
    }, 30000);

    return () => clearInterval(timer);
  }, [userData?.id]);

  async function syncNow() {
    if (syncing) return;
    setSyncing(true);
    setSyncMsg("A sincronizar…");

    const passos = [
      "fabricantes",
      "fornecedores",
      "vendedores",
      "entidadesgrupos",
      "entidades",
      "setoresatividade",
      "clientespagadores",
      "vendedoresrotas",
      "materiaissabores",
      "materiaissegmentos",
      "materiaiscapacidades",
      "materiaisfamilias",
      "materiaismarcas",
      "materiaisgrupos",
      "materiaissubfamilias",
      "materiaissubmarcas",
      "materiaistipos",
      "materiaisembalagens",
      "materiaisunidades",
      "materiais",
      "materiaisidentificadores",
      "materiaisstocks",
      "faturascompras",
      "materiaisprecos",
      "entidadesbloqueiosfaturacao",
      "entidadesbloqueiosfornecimento",
      "entidadesbloqueiosordem",
      "materiaisentradas",
      "faturasvendastransportes",
      "descontosmaxclientes",
      "materiaisdescontospromocionais",
      // "picagensdiarias",
      "faturasvendas",
    ];

    try {
      for (const ep of passos) {
        setSyncMsg(`A sincronizar: ${ep}…`);
        const resp = await fetch(`/api/sincronizacao/${ep}/run`, { method: "POST" });
        const json = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(json?.message || `Falha em ${ep}`);
      }
      setSyncMsg("Sincronização concluída ✅");
    } catch (err) {
      setSyncMsg(`Erro: ${err?.message || "Falha na sincronização"}`);
    } finally {
      setTimeout(() => setSyncing(false), 800);
    }
  }

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
  const handleOpenMenu = (event) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(false);

  const handleOpenChatsMenu = (event) => setOpenChatsMenu(event.currentTarget);
  const handleCloseChatsMenu = () => setOpenChatsMenu(null);

  const handleOpenClienteDebitoInfo = (item) => {
    setClienteDebitoSelecionado(item || null);
    setOpenClienteDebitoInfo(true);
  };

  const handleCloseClienteDebitoInfo = () => {
    setOpenClienteDebitoInfo(false);
    setClienteDebitoSelecionado(null);
  };

  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorReference={null}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2 }}
    >
      <NotificationItem icon={<Icon>email</Icon>} title="Check new messages" />
      <NotificationItem icon={<Icon>podcasts</Icon>} title="Manage Podcast sessions" />
      <NotificationItem icon={<Icon>shopping_cart</Icon>} title="Payment successfully completed" />
    </Menu>
  );

  const renderChatsMenu = () => (
    <Menu
      anchorEl={openChatsMenu}
      open={Boolean(openChatsMenu)}
      onClose={handleCloseChatsMenu}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      sx={{ mt: 1 }}
    >
      <MenuItem disabled sx={{ opacity: 1, cursor: "default" }}>
        <MDBox display="flex" alignItems="center" gap={1}>
          <Icon fontSize="small">chat</Icon>
          <span>Chats por ler: {chatsNaoLidos}</span>
        </MDBox>
      </MenuItem>

      {!telegramLigado && (
        <MenuItem
          onClick={() => {
            handleCloseChatsMenu();
            navigate("/comunicacao/telegram");
          }}
        >
          <MDBox display="flex" alignItems="center" gap={1}>
            <TelegramSvgIcon />
            <span>Conectar Telegram</span>
          </MDBox>
        </MenuItem>
      )}

      <MenuItem>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={resumoTelegram}
              disabled={!telegramLigado}
              onChange={(e) => {
                const novo = e.target.checked;

                const validado = validarPreferencias(novo, resumoEmail);

                if (!validado) return;

                setResumoTelegram(validado.telegram);
                setResumoEmail(validado.email);

                atualizarPreferencias({
                  resumoTelegram: validado.telegram,
                  resumoEmail: validado.email,
                });
              }}
            />
          }
          label={
            <span
              style={{
                fontSize: 13,
                color: !telegramLigado ? "#9e9e9e" : "inherit",
              }}
            >
              Resumo diário (Telegram)
            </span>
          }
          sx={{ m: 0, width: "100%", justifyContent: "space-between" }}
        />
      </MenuItem>

      <MenuItem>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={resumoEmail}
              onChange={(e) => {
                const novo = e.target.checked;

                const validado = validarPreferencias(resumoTelegram, novo);

                if (!validado) return;

                setResumoTelegram(validado.telegram);
                setResumoEmail(validado.email);

                atualizarPreferencias({
                  resumoTelegram: validado.telegram,
                  resumoEmail: validado.email,
                });
              }}
            />
          }
          label={<span style={{ fontSize: 13 }}>Resumo diário (Email)</span>}
          sx={{ m: 0, width: "100%", justifyContent: "space-between" }}
        />
      </MenuItem>

      {chatsNaoLidosLista.slice(0, 5).map((chat) => (
        <NotificationItem
          key={chat.idchat}
          icon={<Icon>forum</Icon>}
          title={`${chat.designacao || "Chat"} (${chat.total_nao_lidas || 0})`}
          onClick={() => {
            handleCloseChatsMenu();
            navigate(`/comunicacao/chats/${chat.idchat}/${chat.idprimeira_nao_lida || ""}`);
          }}
        />
      ))}

      <NotificationItem
        icon={<Icon>forum</Icon>}
        title="Abrir chats"
        onClick={() => {
          handleCloseChatsMenu();
          navigate("/comunicacao/chats");
        }}
      />
    </Menu>
  );

  // --- Auto texto para navbar baseado no próprio navbarBg (nome do tema ou HEX)
  const getNavbarAutoTextColor = () => {
    const bg = uiOverrides?.navbarBg;
    if (!bg) return undefined;
    const g = theme.palette.gradients?.[bg];
    const main = g ? g.main : bg; // se for HEX, usa o HEX diretamente
    return getTextColorForBackground(main); // "#FFFFFF" ou "#000000"
  };

  const resolvedNavbarText =
    uiOverrides?.navbarText !== null ? uiOverrides.navbarText : getNavbarAutoTextColor();

  // Breadcrumbs precisa da prop 'light' para mudar o esquema interno (white vs dark)
  const breadcrumbsLight = resolvedNavbarText
    ? resolvedNavbarText.toLowerCase() === "#ffffff"
    : light;

  // cor real do "círculo" do user (usa a paleta do tema p/ a cor ativa da sidenav)
  const userIconColor = theme.palette?.[sidenavColor]?.main || theme.palette.info.main;

  const chatsIconColor =
    chatsMencoes > 0 ? "#f44336" : chatsNaoLidos > 0 ? "#f59e0b" : resolvedNavbarText || "inherit";

  const chatsBadgeColor = chatsMencoes > 0 ? "error" : chatsNaoLidos > 0 ? "warning" : "default";

  const chatsTooltip =
    chatsMencoes > 0
      ? `Chats (${chatsNaoLidos}) • Menções (${chatsMencoes})`
      : chatsNaoLidos > 0
      ? `Chats por ler (${chatsNaoLidos})`
      : "Chats";

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(th) => {
        const base = navbar(th, { transparentNavbar, absolute, light, darkMode });
        if (!uiOverrides?.navbarBg) return base;

        return {
          ...base,
          background: getGradientFromColor(th, uiOverrides.navbarBg),
          color: resolvedNavbarText || "inherit",
        };
      }}
    >
      <Toolbar sx={(th) => navbarContainer(th)}>
        <MDBox
          color={resolvedNavbarText || "inherit"}
          mb={{ xs: 1, md: 0 }}
          sx={(th) => navbarRow(th, { isMini })}
        >
          <Breadcrumbs
            icon="home"
            title={route[route.length - 1]}
            route={route}
            light={breadcrumbsLight}
          />
        </MDBox>

        {isMini ? null : (
          <MDBox sx={(th) => navbarRow(th, { isMini })} color={resolvedNavbarText || "inherit"}>
            {/* === BLOCO RESPONSIVO (Pesquisa + Ícones) === */}
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={{ xs: 1, md: 2 }}
              alignItems={{ xs: "stretch", md: "center" }}
              sx={{ width: "100%" }}
            >
              {/* Pesquisa: full-width em mobile */}
              <MDBox sx={{ flexGrow: 1 }}>
                <MenuSearch size="sm" fullWidth maxSuggestions={100} />
              </MDBox>

              {/* Fila única de ícones */}
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent={{ xs: "flex-start", md: "flex-end" }}
                sx={{
                  width: { xs: "100%", md: "auto" },
                  flexWrap: "wrap",
                  rowGap: 0.5,
                  overflow: "visible",
                  "& .MuiIconButton-root": { width: 36, height: 36 },
                }}
              >
                <ClientesDebitosAlertasPopper
                  tooltip="Clientes sem vendas, e com débitos."
                  onOpenInfo={handleOpenClienteDebitoInfo}
                />

                <EquipamentosEventosAlertasPopper />

                {/* {isAdminLike && ( */}
                <Tooltip title="Ler código de barras / QR Code">
                  <IconButton
                    size="small"
                    onClick={() => setOpenBarcodeReader(true)}
                    color="inherit"
                    sx={{ "&:hover": { bgcolor: "rgba(255,152,0,0.10)" } }}
                  >
                    <DocumentScannerIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {/* )} */}

                <Tooltip title="Enviar mensagem">
                  <IconButton
                    size="small"
                    onClick={() => setOpenEmail(true)}
                    color="inherit"
                    sx={{ "&:hover": { bgcolor: "rgba(255,152,0,0.10)" } }}
                  >
                    <Icon>mail</Icon>
                  </IconButton>
                </Tooltip>

                {/* <DocsMenu /> */}
                <HeaderTypeSwitch />
                {/* <ClientesPagadoresQuickViewButton /> */}

                {isAdminLike && (
                  <Tooltip title={syncMsg || "Forçar sincronização agora"} arrow>
                    <span>
                      <IconButton
                        size="small"
                        color="info"
                        onClick={syncNow}
                        disabled={syncing}
                        sx={{ ml: 1 }}
                      >
                        <Icon>{syncing ? "hourglass_top" : "autorenew"}</Icon>
                      </IconButton>
                    </span>
                  </Tooltip>
                )}

                <IconButton
                  size="small"
                  disableRipple
                  color="inherit"
                  sx={navbarMobileMenu}
                  onClick={handleMiniSidenav}
                  aria-label="Abrir/fechar menu lateral"
                >
                  <Icon fontSize="medium">{miniSidenav ? "menu_open" : "menu"}</Icon>
                </IconButton>

                {!uiOverrides?.hideConfigurator && (
                  <IconButton
                    size="small"
                    disableRipple
                    color="inherit"
                    sx={navbarIconButton}
                    onClick={handleConfiguratorOpen}
                    aria-label="Configurações"
                  >
                    <Icon>settings</Icon>
                  </IconButton>
                )}

                {isAdminLike && (
                  <Tooltip title={chatsTooltip}>
                    <IconButton
                      size="small"
                      disableRipple
                      color="inherit"
                      sx={{
                        ...navbarIconButton,
                        color: chatsIconColor,
                      }}
                      onClick={handleOpenChatsMenu}
                      aria-label="Chats"
                    >
                      <Badge
                        badgeContent={chatsNaoLidos}
                        color={chatsBadgeColor}
                        overlap="circular"
                        max={99}
                      >
                        <Icon>chat</Icon>
                      </Badge>
                    </IconButton>
                  </Tooltip>
                )}

                <UserMenu />
              </Stack>
            </Stack>

            <EmailDialog
              maxEachMB={18}
              maxTotalMB={20}
              open={openEmail}
              onClose={() => setOpenEmail(false)}
            />

            <DialogBarcodeReader
              open={openBarcodeReader}
              onClose={() => setOpenBarcodeReader(false)}
            />

            <DialogClienteDebitoInfo
              open={openClienteDebitoInfo}
              onClose={handleCloseClienteDebitoInfo}
              item={clienteDebitoSelecionado}
              onOpenContactos={handleOpenContactos}
            />

            <DialogClienteContactos
              open={openContactos}
              onClose={() => {
                setOpenContactos(false);
                setItemContactos(null);
              }}
              item={itemContactos}
              key={`${itemContactos?.refcliente || "sem-cliente"}_${
                openContactos ? "open" : "closed"
              }`}
            />

            {renderMenu()}
            {renderChatsMenu()}
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

DashboardNavbar.defaultProps = { absolute: false, light: false, isMini: false };
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};
export default DashboardNavbar;
